import {createSpreadsheetCore} from './spreadsheetCore';

// Self-contained because this factory also runs inside the spreadsheet iframe.
export function createWorkbookCore() {
    const isObject=value=>Boolean(value && typeof value==='object' && !Array.isArray(value));
    const sheetData=value=>({cellData:isObject(value?.cellData)?value.cellData:{},mergedCells:isObject(value?.mergedCells)?value.mergedCells:{},columnWidths:isObject(value?.columnWidths)?value.columnWidths:{},rowHeights:isObject(value?.rowHeights)?value.rowHeights:{},...(value?.drawings?{drawings:value.drawings}:{})});
    function nameError(name,sheets=[],ignoreId=null) {
        const text=String(name || '').trim();
        if(!text)return 'Enter a sheet name.';
        if(text.length>31)return 'Use 31 characters or fewer.';
        if(/[\\/?*[\]:]/.test(text))return 'Sheet names cannot contain \\ / ? * [ ] or :.';
        if(sheets.some(sheet=>sheet.id!==ignoreId && sheet.name.toLowerCase()===text.toLowerCase()))return 'A sheet already uses that name.';
        return '';
    }
    function normalize(value) {
        const source=isObject(value)?value:{},ids=new Set(),names=new Set();
        let sheets=Array.isArray(source.sheets)?source.sheets.filter(item=>isObject(item) && isObject(item.data)).map((item,index)=>{
            let id=typeof item.id==='string' && item.id?item.id:'sheet-'+(index+1);
            while(ids.has(id))id+='-copy';ids.add(id);
            let name=String(item.name || 'Sheet '+(index+1)).trim().slice(0,31) || 'Sheet '+(index+1),suffix=2,base=name;
            while(names.has(name.toLowerCase()))name=base.slice(0,26)+' ('+(suffix++)+')';names.add(name.toLowerCase());
            return {id,name,data:sheetData(item.data)};
        }):[];
        if(!sheets.length)sheets=[{id:'sheet-1',name:'Sheet 1',data:sheetData(source)}];
        // The first sheet is stored at the root for older readers and answer-cell
        // marking. This also accepts early workbooks that duplicated its data.
        else if(isObject(source.cellData))sheets[0]={...sheets[0],data:sheetData(source)};
        return {sheets,activeSheetId:sheets.some(sheet=>sheet.id===source.activeSheetId)?source.activeSheetId:sheets[0].id};
    }
    function pack(book,activeData) {
        const sheets=book.sheets.map(sheet=>sheet.id===book.activeSheetId && activeData?{...sheet,data:sheetData(activeData)}:sheet);
        const first=sheets[0];
        if(sheets.length===1 && first.name==='Sheet 1')return {...first.data};
        // Store the first sheet only once. Duplicating a large answer or drawing
        // here can cross Firestore's document limit just by adding a blank tab.
        const savedSheets=sheets.map((sheet,index)=>index===0?{...sheet,data:{}}:sheet);
        return {...first.data,workbookVersion:1,sheets:savedSheets,activeSheetId:book.activeSheetId};
    }
    function nextName(sheets) {const names=new Set(sheets.map(sheet=>sheet.name.toLowerCase()));let number=1;while(names.has('sheet '+number))number++;return 'Sheet '+number;}
    function nextId(sheets) {const ids=new Set(sheets.map(sheet=>sheet.id));let number=1;while(ids.has('sheet-'+number))number++;return 'sheet-'+number;}
    return {normalize,pack,sheetData,nameError,nextName,nextId};
}

const workbook=createWorkbookCore(),formula=createSpreadsheetCore();
export const getWorkbookSheets=value=>workbook.normalize(value).sheets;
export function hasWorkbookResponse(value) {
    return getWorkbookSheets(value).some(({data})=>Object.values(data.cellData).some(cell=>String(cell?.value ?? '').trim()!=='') || Boolean(data.drawings?.strokes?.length));
}

// Used by initial marking and revaluation; cached results are never trusted.
export function spreadsheetToText(value) {
    return getWorkbookSheets(value).map(({name,data})=>{
        const ids=Object.keys(data.cellData).filter(id=>formula.coords(id)).sort((a,b)=>{const x=formula.coords(a),y=formula.coords(b);return x.row-y.row || x.col-y.col;});
        const results=formula.calculate(data.cellData),lines=[];
        ids.forEach(id=>{
            const cell=data.cellData[id],raw=cell?.value ?? '';
            if(String(raw).trim()==='')return;
            const result=Object.prototype.hasOwnProperty.call(results,id)?results[id]:raw;
            lines.push(id+': '+String(result)+(String(raw).trim().startsWith('=')?' (formula: '+raw+')':''));
        });
        if(data.drawings?.strokes?.length)lines.push('[This sheet also contains handwritten annotations.]');
        return lines.length?'Sheet: '+name+'\n'+lines.join('\n'):'';
    }).filter(Boolean).join('\n\n');
}
